import { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';

import {
  createNewSchematicVersion,
  createSchematicComponent,
  deleteSchematicComponent,
  fetchSchematicData,
  updateSchematicComponent,
} from 'features/wells/wellbore.actions';
import { loadAllElementsList } from 'features/wells/wellbore.actions.js';

import { selectSchematicData } from 'features/wells/wellbore.selectors';
import { getCurrentClientSelectedUom } from 'app/app.selectors';
import { getAllElementsList } from 'features/wells/wellbore.selectors';
import { getUnitsOfMeasurePreference } from 'features/equipment/equipment.selectors';

import { BasePage, LoadingOverlay } from 'altus-ui-components';
import WellBoreSchematic from 'features/wells/schematics/components/WellBoreSchematic';
import {
  newSchematicVersionComponentsMapper,
  schematicUoM,
} from 'features/wells/schematics//helpers/utils';
import { UNIT_OF_MEASURE } from 'features/equipment/equipment.constants';
import { useHeader } from 'app/hooks/useHeader';
import { EMPTY_LIST } from 'app/app.constants';

const SchematicContainer = ({
  breadcrumb,
  intData,
  dispatchFetchSchematicData,
  dispatchCreateSchematicComponent,
  dispatchUpdateSchematicComponent,
  dispatchDeleteSchematicComponent,
  dispatchLoadAllElements,
  dispatchCreateNewSchematicVersion,
  allElements,
  wellboreId,
}) => {
  useHeader({ subTitle: breadcrumb });
  const [selectedSchematicVersion, setSelectedSchematicVersion] = useState('');
  const { data, error, isFetching } = intData;
  const hasIds = wellboreId;
  const measurePreferences = useSelector(getCurrentClientSelectedUom);
  const unitsOfMeasurePreference = useSelector(getUnitsOfMeasurePreference);
  const selectUoMPreference =
    unitsOfMeasurePreference === UNIT_OF_MEASURE.IMPERIAL ||
    unitsOfMeasurePreference === UNIT_OF_MEASURE.METRIC
      ? unitsOfMeasurePreference
      : measurePreferences?.length;
  const UoM = schematicUoM(selectUoMPreference);
  const hasData = data !== undefined && data !== null && data.length !== 0;
  const versionsList =
    hasData && data[0]?.validFrom
      ? data.map((version) => version.validFrom)
      : [];
  const selectedSchematicVersionIndex = versionsList.indexOf(
    selectedSchematicVersion,
  );
  const isLatestVersion = selectedSchematicVersionIndex === 0;

  const schematicVersionComponentsData =
    hasData && selectedSchematicVersionIndex >= 0
      ? data[selectedSchematicVersionIndex].components
      : [];

  const componentsData =
    hasData && selectedSchematicVersionIndex < 0
      ? data[0].components
      : schematicVersionComponentsData;

  const versionComment =
    hasData && selectedSchematicVersionIndex >= 0
      ? data[selectedSchematicVersionIndex].comment
      : '';

  const selectedVersionCreator =
    hasData && selectedSchematicVersionIndex >= 0
      ? data[selectedSchematicVersionIndex].createdByUser
      : EMPTY_LIST;

  const versionCreator =
    hasData && selectedSchematicVersionIndex < 0
      ? data[0].createdByUser
      : selectedVersionCreator;

  const handleSelectChange = (value) => setSelectedSchematicVersion(value);
  const handleSubmit = (formState, callback, componentIndex) => {
    const isEditMode = componentIndex !== null && componentIndex >= 0;
    if (isEditMode) {
      return dispatchUpdateSchematicComponent(
        formState,
        callback,
        componentIndex,
      );
    }
    dispatchCreateSchematicComponent(formState, callback);
  };
  const handleDelete = (componentIndex, callback) => {
    return dispatchDeleteSchematicComponent(componentIndex, callback);
  };

  const handleVersionFormSubmit = (comment, callback) => {
    dispatchCreateNewSchematicVersion(
      wellboreId,
      {
        comment,
        components: newSchematicVersionComponentsMapper(
          componentsData,
          UoM.length,
          UoM.width,
        ),
      },
      callback,
    );
  };

  const handleCancel = () => {
    dispatchFetchSchematicData(wellboreId);
  };

  useEffect(() => {
    dispatchLoadAllElements();
  }, [dispatchLoadAllElements]);

  useEffect(() => {
    if (hasIds) {
      dispatchFetchSchematicData(wellboreId);
    }
  }, [dispatchFetchSchematicData, hasIds, wellboreId]);

  useEffect(() => {
    if (hasData) {
      setSelectedSchematicVersion(data[0].validFrom);
    }
  }, [hasData, data, setSelectedSchematicVersion]);

  if (error) {
    //will display proper component
    return <BasePage>'Error ocured.'</BasePage>;
  }

  return (
    <BasePage>
      <WellBoreSchematic
        isFetching={isFetching}
        data={componentsData}
        comment={versionComment}
        versionsList={versionsList}
        selectedSchematicVersion={selectedSchematicVersion}
        units={UoM}
        allElements={allElements}
        isLatestVersion={isLatestVersion}
        versionCreator={versionCreator}
        handleSelectChange={handleSelectChange}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
        handleVersionFormSubmit={handleVersionFormSubmit}
        handleCancel={handleCancel}
      />
      <LoadingOverlay timeout={0} loading={isFetching} />
    </BasePage>
  );
};

export default connect(
  (state) => ({
    intData: selectSchematicData(state),
    allElements: getAllElementsList(state),
  }),
  {
    dispatchFetchSchematicData: fetchSchematicData,
    dispatchLoadAllElements: loadAllElementsList,
    dispatchCreateSchematicComponent: createSchematicComponent,
    dispatchUpdateSchematicComponent: updateSchematicComponent,
    dispatchDeleteSchematicComponent: deleteSchematicComponent,
    dispatchCreateNewSchematicVersion: createNewSchematicVersion,
  },
)(SchematicContainer);
